/**
 * Box 8 (docs/superpowers/specs/2026-10-28-native-budget-mmm-design.md §5.7), the prototype's allocationBox: each
 * channel's weekly budget from the simulator, split across the channel's ad accounts by their live 28-day figures (plan
 * R17). An estimate inside the channel, not an MMM result, so nothing here has a range. Pure.
 * The caller passes only the scope currency's rows (spec §5.9); a row of unknown currency is skipped anyway (T10f).
 *
 * @module services/budget/accountAllocation
 */
import { addNullable, ratioOf, totalsOf } from '@/services/overview/overviewMath';
import type { FactRow } from '@/services/overview/overviewTypes';
import { metricValueOf } from '@/services/overview/reportedMetric';
import type { ResultMappingInfo, ResultMetric } from '@/services/targets/resultMetric';
import { MAX_SHIFT } from './budgetContract';
import { costPerResultOf, mainObjectiveOf, resultMetricOf } from './interim';
import { accountOrder, type AdAccount } from './scopeRows';
import type { Allocation } from './simulator';

export interface AllocationRow {
  accountId: string;
  accountName: string | null;
  channel: string;
  mainObjective: string | null;
  weeklySpend: number | null;
  /** Of the channel's in-scope spend; null out of scope, with no spend reported, or when the in-scope accounts spent nothing (T10d). */
  share: number | null;
  /** What cpr is per (rules-04 E3); null where resultFiguresOf gives no Result (T12f). */
  metric: ResultMetric | null;
  cpr: number | null;
  /** The channel's CPR ÷ the account's, in the account's main objective; null = not comparable, so it keeps its share. */
  index: number | null;
  recommended: number;
  inScope: boolean;
}

/** R17's 28-day window, in weeks. */
const WEEKS = 4;

type Account = AdAccount & { rows: FactRow[]; spend: number | null; inScope: boolean };

/** Result per unit of spend over resultFiguresOf's reporting rows; null with no Result or no spend behind it. */
const efficiencyOf = (rows: readonly FactRow[], objective: string, mapping: ResultMappingInfo): number | null => {
  const metric = resultMetricOf(objective, mapping);
  if (metric === null) return null;
  const reporting = rows.filter((r) => r.objective === objective && metricValueOf(r, metric) !== null);
  return ratioOf(reporting.reduce<number | null>((s, r) => addNullable(s, metricValueOf(r, metric)), null), totalsOf(reporting).spend);
};

/**
 * T10c: the account's efficiency over its in-scope channel's, in its main objective. That is the channel's CPR ÷ the
 * account's, with Awareness' per 1,000 cancelled, and it is 0 (not null) when the account's result is 0.
 */
const indexOf = (a: Account, main: string | null, scoped: readonly Account[], mapping: ResultMappingInfo): number | null => {
  if (!a.inScope || main === null) return null;
  // "Shares no objective with another account" (spec §5.7): another in-scope account spent in it too.
  if (scoped.filter((s) => s.rows.some((r) => r.objective === main && (r.spend ?? 0) > 0)).length < 2) return null;
  return ratioOf(efficiencyOf(a.rows, main, mapping), efficiencyOf(scoped.flatMap((s) => s.rows), main, mapping));
};

/** raw = share × index (the share alone without one), held within ±MAX_SHIFT of the share before normalising. */
const rawOf = (share: number | null, index: number | null): number =>
  (share ?? 0) * Math.min(1 + MAX_SHIFT, Math.max(1 - MAX_SHIFT, index ?? 1));

const channelRowsOf = (channel: string, budget: number, accounts: readonly Account[], mapping: ResultMappingInfo): AllocationRow[] => {
  const scoped = accounts.filter((a) => a.inScope);
  const total = scoped.reduce<number | null>((s, a) => addNullable(s, a.spend), null);
  const items = accounts.map((a) => {
    const main = mainObjectiveOf(a.rows);
    const share = a.inScope ? ratioOf(a.spend, total) : null;
    const index = indexOf(a, main, scoped, mapping);
    return { a, main, share, index, raw: rawOf(share, index) };
  });
  const rawTotal = items.reduce((s, i) => s + i.raw, 0);
  return items.map(({ a, main, share, index, raw }) => ({
    accountId: a.id, accountName: a.accountName, channel, mainObjective: main,
    weeklySpend: a.spend === null ? null : a.spend / WEEKS, share,
    metric: resultMetricOf(main, mapping), cpr: costPerResultOf(a.rows, main, mapping), index,
    recommended: rawTotal > 0 ? (budget * raw) / rawTotal : 0, inScope: a.inScope,
  }));
};

/** The budget's channels in its order (the bundle's), each one's accounts by accountOrder; no other channel's accounts. */
export const allocationOf = (
  channelBudget: Allocation, current: readonly FactRow[], accounts: readonly AdAccount[], scopeIds: readonly string[],
  mapping: ResultMappingInfo,
): AllocationRow[] => {
  const known = current.filter((r) => r.currency !== null);
  return Object.entries(channelBudget).flatMap(([channel, budget]) => {
    const own = accounts.filter((a) => a.platform === channel).map((a): Account => {
      const rows = known.filter((r) => r.accountId === a.id);
      // An account of unknown currency never joins the scope (spec §5.9), as box S shows it.
      return { ...a, rows, spend: totalsOf(rows).spend, inScope: a.currency !== null && scopeIds.includes(a.id) };
    });
    return channelRowsOf(channel, budget, own.sort(accountOrder), mapping);
  });
};
